import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Languages, Loader2 } from "lucide-react";
import { TranslatorSessionList } from "@/components/translator/TranslatorSessionList";
import { NewTranslatorSessionDialog } from "@/components/translator/NewTranslatorSessionDialog";
import { TranslatorClientCard } from "@/components/translator/TranslatorClientCard";
import { TranslatorPanel } from "@/components/translator/TranslatorPanel";
import { TranslatorHistory } from "@/components/translator/TranslatorHistory";
import { capitalize } from "@/components/translator/shared";

const errMsg = (e) => formatApiErrorDetail(e?.response?.data?.detail);

export default function UniversalTranslatorPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [langBusy, setLangBusy] = useState(false);

  const loadList = useCallback(() => {
    api.get("/translator/sessions").then((r) => setSessions(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const open = async (id) => {
    setLoadingId(id);
    try {
      const { data } = await api.get(`/translator/sessions/${id}`);
      setCurrent(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingId(null);
    }
  };

  const sid = current?.session.id;
  const pushMessage = (msg) => setCurrent((c) => ({ ...c, messages: [...c.messages, msg] }));

  const translate = (kind) => async (text) => {
    try {
      const { data } = await api.post(`/translator/sessions/${sid}/${kind}`, { text });
      pushMessage(data.message);
      loadList();
      if (kind === "incoming" && data.detected_lang && !["es", current.session.client_lang].includes(data.detected_lang))
        toast.info(`Este mensaje parece estar en ${data.detected_lang_name}. Puedes cambiar el idioma del cliente.`);
      return data.message.translated_text;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };

  const changeLang = async (lang) => {
    setLangBusy(true);
    try {
      const { data } = await api.patch(`/translator/sessions/${sid}`, { client_lang: lang });
      setCurrent(data);
      loadList();
      toast.success(`Idioma cambiado a ${capitalize(data.session.client_lang_name)}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLangBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta conversación y su historial?")) return;
    try {
      await api.delete(`/translator/sessions/${sid}`);
      setCurrent(null);
      loadList();
      toast.success("Conversación eliminada");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onCreated = (data) => {
    setNewOpen(false);
    setCurrent(data);
    loadList();
    toast.success(`Idioma detectado: ${capitalize(data.session.client_lang_name)}`);
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1500px]" data-testid="translator-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Herramientas · Atención al cliente</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
          <Languages className="text-[#E63946]" size={32} /> Traductor Universal
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Pega el mensaje del cliente (WhatsApp, email…), lee la traducción en español y responde en su idioma.
          El historial se borra automáticamente a los 7 días.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
        <div className={`${current ? "hidden lg:flex" : "flex"} flex-col lg:max-h-[calc(100vh-220px)]`}>
          <TranslatorSessionList sessions={sessions} activeId={sid} onSelect={open} onNew={() => setNewOpen(true)} showOwner={user?.role === "admin"} />
        </div>

        <div className="min-w-0 space-y-5">
          {loadingId && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>}
          {!current && !loadingId && (
            <div className="hidden lg:flex border-2 border-dashed border-zinc-300 p-16 flex-col items-center justify-center text-center text-zinc-500" data-testid="translator-placeholder">
              <Languages size={40} className="mb-3 text-zinc-300" />
              Selecciona una conversación o crea una nueva.
            </div>
          )}
          {current && !loadingId && (
            <>
              <button onClick={() => setCurrent(null)} className="lg:hidden inline-flex items-center gap-1 text-sm font-semibold" data-testid="translator-back-btn">
                <ArrowLeft size={16} /> Conversaciones
              </button>
              <TranslatorClientCard session={current.session} booking={current.booking} onChangeLang={changeLang} onDelete={remove} langBusy={langBusy} />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" key={sid}>
                <TranslatorPanel
                  id="incoming"
                  title="Mensaje del cliente → Español"
                  subtitle={`Pega lo que te escribió en ${current.session.client_lang_name}`}
                  placeholder="Pega aquí el mensaje del cliente…"
                  actionLabel="Traducir al español"
                  onTranslate={translate("incoming")}
                />
                <TranslatorPanel
                  id="outgoing"
                  accent
                  title={`Tu respuesta → ${capitalize(current.session.client_lang_name)}`}
                  subtitle="Escribe en español y copia la traducción"
                  placeholder="Escribe tu respuesta en español…"
                  actionLabel={`Traducir al ${current.session.client_lang_name}`}
                  onTranslate={translate("outgoing")}
                />
              </div>
              <TranslatorHistory messages={current.messages} />
            </>
          )}
        </div>
      </div>

      <NewTranslatorSessionDialog open={newOpen} onOpenChange={setNewOpen} onCreated={onCreated} />
    </div>
  );
}
